import type { Locale } from '../translations';
import { getGamePageContent, type GamePageContent } from './gameInfo';

export type BuiltInGuideTopic = {
    slug: string;
    title: string;
    summary: string;
    content: string;
    icon: string;
    categoryKey: string;
    categoryTitle: string;
    /** Se definido, o card principal leva para outra rota (ex.: /classes) */
    href?: string;
};

/** Slugs do CMS que equivalem a um tópico embutido (evita duplicar no menu) */
export const BUILT_IN_SLUG_ALIASES: Record<string, string> = {
    'about-empire-icarus': 'overview',
    'familiars-and-mounts': 'mounts',
    'world-and-dungeons': 'world',
    'start-playing': 'start-playing',
    'is-the-game-free': 'faq',
    'how-many-classes': 'classes',
};

const BUILT_IN_SLUGS = new Set([
    'overview',
    'mounts',
    'world',
    'gameplay',
    'start-playing',
    'classes',
    'faq',
]);

export function isBuiltInGuideSlug(slug: string): boolean {
    return BUILT_IN_SLUGS.has(slug) || slug in BUILT_IN_SLUG_ALIASES;
}

function sectionToContent(paragraphs: string[], bullets?: string[]): string {
    const parts = [...paragraphs];
    if (bullets?.length) {
        parts.push(bullets.map((b) => `- ${b}`).join('\n'));
    }
    return parts.join('\n\n');
}

function buildTopics(c: GamePageContent, locale: Locale): BuiltInGuideTopic[] {
    const isPt = locale === 'pt-BR';
    const basics = isPt ? 'Sobre o jogo' : 'About the game';
    const start = isPt ? 'Comece a jogar' : 'Start playing';
    const help = isPt ? 'Ajuda' : 'Help';

    const sectionTopics: BuiltInGuideTopic[] = c.sections.map((s) => ({
        slug: s.id,
        title: s.title,
        summary: s.paragraphs[0] ?? '',
        content: sectionToContent(s.paragraphs, s.bullets),
        icon:
            s.id === 'overview'
                ? 'ri-information-line'
                : s.id === 'mounts'
                  ? 'ri-dragon-line'
                  : s.id === 'world'
                    ? 'ri-earth-line'
                    : 'ri-sword-line',
        categoryKey: 'basics',
        categoryTitle: basics,
    }));

    return [
        ...sectionTopics,
        {
            slug: 'start-playing',
            title: c.server.title,
            summary: c.server.paragraphs[0] ?? '',
            content: sectionToContent(c.server.paragraphs, c.server.bullets),
            icon: 'ri-rocket-line',
            categoryKey: 'start',
            categoryTitle: start,
        },
        {
            slug: 'classes',
            title: c.classesTeaser.title,
            summary: c.classesTeaser.text,
            content: `${c.classesTeaser.text}\n\n${isPt ? 'Abra a página de classes para ver todas as habilidades e papéis em grupo.' : 'Open the Classes page to see all skills and party roles.'}`,
            icon: 'ri-group-line',
            categoryKey: 'start',
            categoryTitle: start,
            href: '/classes',
        },
        {
            slug: 'faq',
            title: isPt ? 'Perguntas frequentes' : 'FAQ',
            summary: isPt
                ? 'Respostas sobre conta, download, classes e suporte.'
                : 'Answers about account, download, classes and support.',
            content: c.faq.map((f) => `## ${f.q}\n\n${f.a}`).join('\n\n'),
            icon: 'ri-question-answer-line',
            categoryKey: 'help',
            categoryTitle: help,
        },
    ];
}

export function getBuiltInGuideTopics(locale: Locale): BuiltInGuideTopic[] {
    return buildTopics(getGamePageContent(locale), locale);
}

export function getBuiltInGuideCategories(locale: Locale): { key: string; title: string; topics: BuiltInGuideTopic[] }[] {
    const topics = getBuiltInGuideTopics(locale);
    const order = ['basics', 'start', 'help'];
    const titles = new Map(topics.map((t) => [t.categoryKey, t.categoryTitle]));
    return order
        .filter((key) => topics.some((t) => t.categoryKey === key))
        .map((key) => ({
            key,
            title: titles.get(key) ?? key,
            topics: topics.filter((t) => t.categoryKey === key),
        }));
}

export function getBuiltInGuideTopic(slug: string, locale: Locale): BuiltInGuideTopic | null {
    const resolved = BUILT_IN_SLUG_ALIASES[slug] ?? slug;
    return getBuiltInGuideTopics(locale).find((t) => t.slug === resolved) ?? null;
}

export function cmsSlugOverlapsBuiltIn(cmsSlug: string): boolean {
    const resolved = BUILT_IN_SLUG_ALIASES[cmsSlug] ?? cmsSlug;
    return BUILT_IN_SLUGS.has(resolved);
}
